import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiClock, FiMapPin, FiStar, FiX } from 'react-icons/fi';
import { IoRestaurantOutline, IoBicycleOutline, IoReceiptOutline } from 'react-icons/io5';
import { EmptyState, SkeletonGrid } from '../../components/ui';
import BannerCarousel from '../../components/BannerCarousel';
import { api } from '../../services/api';

interface Restaurant {
  id: number;
  name: string;
  description?: string;
  category?: string;
  imageUrl?: string;
  coverImage?: string;
  address?: string;
  openTime?: string;
  closeTime?: string;
  deliveryTimeMin?: number;
  deliveryFee?: number | string;
  deliveryFeeDesde?: boolean; // modo «por distância»: o valor é o mínimo
  itemCount?: number;
  ratingMedia?: number | string | null;
  ratingCount?: number;
}

/** A nota só aparece a partir de 5 avaliações (com menos, uma má nota pesa demais). */
const MIN_AVALIACOES = 5;

/** Aberto agora? Horário em HH:MM (aceita fecho depois da meia-noite). Sem horário = aberto. */
function estaAberto(open?: string, close?: string): boolean {
  if (!open || !close) return true;
  const min = (h: string) => { const [a, b] = h.split(':').map(Number); return a * 60 + (b || 0); };
  const agora = new Date().getHours() * 60 + new Date().getMinutes();
  const a = min(open), f = min(close);
  return a <= f ? agora >= a && agora < f : agora >= a || agora < f;
}

const tempoEntrega = (m?: number) => (m ? `${m}-${m + 15} min` : '30-45 min');
const taxaEntrega = (fee?: number | string) => {
  const n = Number(fee);
  return !n ? 'Entrega grátis' : `${n.toFixed(0)}$ entrega`;
};

const Food: React.FC = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [banners, setBanners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoria, setCategoria] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        // marketplace=1: a montra mostra todos os restaurantes activos, mesmo a
        // contas com papel Food (estafeta...), que a gestão limita à sua entidade.
        const r = await api.get('/food/restaurants', { params: { marketplace: '1', limit: 100 } });
        setRestaurants(r.data.data || []);
      } catch {
        setRestaurants([]);
      } finally {
        setLoading(false);
      }
    })();
    api.get('/food/banners/active').then((r) => setBanners(r.data.data || [])).catch(() => setBanners([]));
  }, []);

  // Categorias reais dos restaurantes (só aparecem quando há mais de uma).
  const categorias = useMemo(
    () => Array.from(new Set(restaurants.map((r) => r.category).filter(Boolean) as string[])).sort(),
    [restaurants],
  );

  const visiveis = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return restaurants
      .filter((r) => categoria === 'all' || r.category === categoria)
      .filter((r) => !q || r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q)
        || (r.address || '').toLowerCase().includes(q))
      // abertos primeiro
      .sort((a, b) => Number(estaAberto(b.openTime, b.closeTime)) - Number(estaAberto(a.openTime, a.closeTime)));
  }, [restaurants, categoria, searchTerm]);

  return (
    <div className="space-y-6 pb-24">
      {/* Cabeçalho */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pagali-purple to-pagali-purple-light px-5 py-7 md:px-10 md:py-10">
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" aria-hidden />
        <div className="absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-pagali-primary/20" aria-hidden />
        <div className="relative max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white">
            <IoBicycleOutline /> Entrega e levantamento
          </span>
          <h1 className="mt-3 text-2xl font-bold text-white md:text-4xl">Pagali Food</h1>
          <p className="mt-1 text-sm text-white/80 md:text-base">
            Peça aos restaurantes perto de si e pague com o seu cartão, em segurança.
          </p>
          <div className="relative mt-5">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-pagali-gray-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Procurar restaurante ou zona…"
              aria-label="Procurar restaurante"
              className="w-full rounded-2xl border-0 bg-white py-3.5 pl-12 pr-12 text-pagali-gray-800 shadow-card focus:outline-none focus:ring-2 focus:ring-pagali-primary"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label="Limpar pesquisa"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-pagali-gray-400 hover:text-pagali-gray-600"
              >
                <FiX />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Banners do Admin (Pagali Food › Banners), só quando existem */}
      {banners.length > 0 && <BannerCarousel banners={banners} />}

      {/* Categorias reais */}
      {categorias.length > 1 && (
        <div className="-mx-4 overflow-x-auto px-4 scrollbar-hide">
          <div className="flex gap-2">
            {['all', ...categorias].map((c) => (
              <button
                key={c}
                onClick={() => setCategoria(c)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  categoria === c
                    ? 'bg-pagali-purple text-white'
                    : 'border border-pagali-gray-200 bg-white text-pagali-gray-600 hover:border-pagali-purple hover:text-pagali-purple'
                }`}
              >
                {c === 'all' ? 'Todos' : c}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Restaurantes */}
      <section>
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold text-pagali-gray-800 md:text-xl">Restaurantes</h2>
          {!loading && (
            <span className="text-sm text-pagali-gray-500">
              {visiveis.length} {visiveis.length === 1 ? 'restaurante' : 'restaurantes'}
            </span>
          )}
        </div>

        {loading ? (
          <SkeletonGrid count={6} />
        ) : visiveis.length === 0 ? (
          <EmptyState
            icon={<IoRestaurantOutline className="text-4xl" />}
            title="Nenhum restaurante encontrado"
            description={searchTerm ? 'Experimente procurar por outro nome.' : 'Ainda não há restaurantes disponíveis.'}
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {visiveis.map((r) => {
              const aberto = estaAberto(r.openTime, r.closeTime);
              const capa = r.imageUrl || r.coverImage;
              return (
                <button
                  key={r.id}
                  onClick={() => navigate(`/food/restaurant/${r.id}`)}
                  className="group overflow-hidden rounded-2xl bg-white text-left shadow-card transition-all hover:-translate-y-0.5 hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-pagali-purple"
                >
                  <div className="relative aspect-[16/9] bg-pagali-gray-100">
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-pagali-purple/10 to-pagali-primary/10 text-pagali-purple/40">
                      <IoRestaurantOutline className="text-5xl" />
                    </div>
                    {capa && (
                      <img
                        src={capa}
                        alt={r.name}
                        loading="lazy"
                        className={`relative h-full w-full object-cover transition-transform duration-300 group-hover:scale-105 ${aberto ? '' : 'grayscale'}`}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <span
                      className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        aberto ? 'bg-pagali-success text-white' : 'bg-pagali-gray-800/80 text-white'
                      }`}
                    >
                      {aberto ? 'Aberto' : `Fechado · abre ${r.openTime?.slice(0, 5)}`}
                    </span>
                    {r.ratingMedia != null && Number(r.ratingCount) >= MIN_AVALIACOES ? (
                      <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs font-semibold text-pagali-gray-800">
                        <FiStar className="text-pagali-warning" /> {Number(r.ratingMedia).toFixed(1)}
                        <span className="font-normal text-pagali-gray-500">({r.ratingCount})</span>
                      </span>
                    ) : null}
                  </div>
                  <div className="p-4">
                    <h3 className="truncate text-base font-semibold text-pagali-gray-800 group-hover:text-pagali-purple">
                      {r.name}
                    </h3>
                    {r.address && (
                      <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-pagali-gray-500">
                        <FiMapPin className="shrink-0" /> {r.address}
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <span className="flex items-center gap-1 rounded-full bg-pagali-gray-100 px-2.5 py-1 text-pagali-gray-600">
                        <FiClock /> {tempoEntrega(r.deliveryTimeMin)}
                      </span>
                      <span className="flex items-center gap-1 rounded-full bg-pagali-gray-100 px-2.5 py-1 text-pagali-gray-600">
                        <IoBicycleOutline /> {r.deliveryFeeDesde ? `desde ${taxaEntrega(r.deliveryFee)}` : taxaEntrega(r.deliveryFee)}
                      </span>
                      {!!r.itemCount && (
                        <span className="flex items-center gap-1 rounded-full bg-pagali-gray-100 px-2.5 py-1 text-pagali-gray-600">
                          <IoReceiptOutline /> {r.itemCount} pratos
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Food;
